import { ChangeEvent, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Box,
  Checkbox,
  createStyles,
  Grid,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import { KeyboardArrowRightOutlined } from '@material-ui/icons';
import { COMMAND_TYPE_OPTIONS, CONFIRMATION } from '../../../../../utils/Utils';
import { TextInputTG, TGSelect } from 'views/components/formElements';
import { map } from 'lodash';
import { useConfirmDialog } from 'context/ConfirmContext';
import { CommandReference } from '@terragotech/gen5-config-lib';
import AdditionalActionsModal from './AdditionalActionsModal';

export type Option = { key: string; value: string };

export interface CommandTypeOption {
  selected: boolean;
  type?: 'select' | 'text' | 'number' | 'object';
  label: string;
  value?: Option[] | string | AdditionalActions;
  style?: string;
}

export interface AdditionalActions {
  workflowConfirmationText: string;
  actions: AdditionalAction[];
}

export interface AdditionalAction {
  label: string;
  type: 'COMMAND' | 'REDIRECT';
  aggregateName?: string;
  action?: CommandReference;
}

export type CommandType = 'BUTTON' | 'FORM' | 'IMPORT' | 'TIMER' | 'ZEBRA';
export type CreateCommand = 'aggregate' | 'nonaggregate';

interface CommandTypeConfigurationProps {
  commandType: CommandType;
  setCommandType: (val: CommandType) => void;
  isImportIntegration: boolean;
  setIsImportIntegration: (checked: boolean) => void;
  fileType: string;
  onChangeTextFileType: (val: string) => void;
  commandTypeOptions: CommandTypeOption[];
  onChangeOption: (checked: boolean, label: string) => void;
  createCommandType: string;
  setCreateCommandType: (val: CreateCommand) => void;
  onChangeText: (value: string, key: string) => void;
  emptyIsNull: boolean;
  setEmptyIsNull: (val: boolean) => void;
  resetFormDefinition: () => void;
  additionalActions: AdditionalActions;
  onSaveActions: (actions: AdditionalActions) => void;
  isCreationCommand: boolean;
  aggregateTypeName: string;
}

const CommandTypeConfiguration = ({
  commandType,
  setCommandType,
  isImportIntegration,
  setIsImportIntegration,
  fileType,
  onChangeTextFileType,
  onChangeOption,
  createCommandType,
  setCreateCommandType,
  onChangeText,
  commandTypeOptions,
  emptyIsNull,
  setEmptyIsNull,
  resetFormDefinition,
  additionalActions,
  onSaveActions,
  isCreationCommand,
  aggregateTypeName,
}: CommandTypeConfigurationProps) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(true);
  const { openConfirmation } = useConfirmDialog();

  const onCommandTypeChange = async (value: string) => {
    const key = COMMAND_TYPE_OPTIONS.find((o) => o.value === value)?.key as CommandType;
    setCommandType(key);
  };
  const onChangeCmdIntegration = async (checked: boolean) => {
    setIsImportIntegration(checked);
  };

  const onCommandNameChange = (options: Option[], value: string) => {
    const key = options.find((o) => o.value === value)?.key as CreateCommand;
    setCreateCommandType(key);
  };

  const handleChange = (event: ChangeEvent<{}>, newExpanded: boolean) => {
    setExpanded(newExpanded);
  };

  const handleConfirmation = async (
    action: 'import' | 'integration' | '',
    value: boolean | string
  ) => {
    const props = CONFIRMATION.commandType({ action });
    const dialogContainerStyle = action === 'import' ? { width: 550, padding: 20 } : undefined;
    const status = await openConfirmation({ ...props, dialogContainerStyle });
    if (status === 'confirm') {
      switch (action) {
        case 'import': {
          onCommandTypeChange(value as string);
          resetFormDefinition();
          break;
        }
        case 'integration': {
          onChangeCmdIntegration(value as boolean);
          break;
        }
        default:
      }
    }
  };

  return (
    <Accordion className={classes.commandTypeContainer} expanded={expanded} onChange={handleChange}>
      <AccordionSummary
        expandIcon={<KeyboardArrowRightOutlined className={classes.expandIcon} />}
        aria-label="Expand"
        aria-controls="additional-actions1-content"
        id="additional-actions1-header"
        className={classes.accordSummary}
      >
        <Box className={classes.accordHead} onClick={(e) => e.stopPropagation()}>
          <Typography className={classes.commandTypeTxt}>Command Type</Typography>
          <TGSelect
            id="commandType"
            options={map(COMMAND_TYPE_OPTIONS, 'value')}
            containerStyle={classes.selectContainer}
            value={COMMAND_TYPE_OPTIONS.find((o) => o.key === commandType)?.value || ''}
            onChange={(value) => {
              if (value === 'Import') {
                handleConfirmation('import', value);
              } else {
                onCommandTypeChange(value);
              }
            }}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails className={classes.accordDetail}>
        {commandType === 'IMPORT' ? (
          <Box className={classes.fileTypeContainer}>
            {!isImportIntegration && (
              <Box className={classes.intgrationCheckbox}>
                <Checkbox
                  checked={emptyIsNull}
                  className={classes.checkBox}
                  onChange={(e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
                    setEmptyIsNull(checked);
                  }}
                  color="primary"
                />
                <Typography className={classes.commandLabel}>Treat Empty Cells as Null</Typography>
              </Box>
            )}
            <Box className={[classes.intgrationCheckbox, classes.firstElement].join(' ')}>
              <Checkbox
                checked={isImportIntegration}
                className={classes.checkBox}
                onChange={(e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
                  if (checked) {
                    handleConfirmation('integration', checked);
                  } else {
                    onChangeCmdIntegration(checked);
                  }
                }}
                color="primary"
              />
              <Typography className={classes.commandLabel}>Integration Import</Typography>
            </Box>
            {isImportIntegration && (
              <Box className={classes.fileTypeBox}>
                <Typography className={classes.fileTypeLabel}>
                  Comma-separated allowed file types
                </Typography>
                <TextInputTG
                  value={fileType as string}
                  inputStyle={classes.fileTypeInput}
                  disabled={!isImportIntegration}
                  onChange={onChangeTextFileType}
                />
              </Box>
            )}
          </Box>
        ) : (
          <Grid container>
            {commandTypeOptions.map((commandOption, index) => {
              return (
                <Grid
                  key={commandOption.label}
                  item
                  sm={4}
                  className={classes.commandOptionContainer}
                >
                  <Box className={classes.rootContainer}>
                    <Checkbox
                      checked={commandOption.selected}
                      className={classes.checkBox}
                      onChange={(e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
                        onChangeOption(checked, commandOption.label);
                      }}
                      color="primary"
                    />
                    <Typography className={classes.commandLabel}>{commandOption.label}</Typography>
                  </Box>
                  {commandOption.type === 'select' && commandOption.selected && (
                    <TGSelect
                      id="aggregate"
                      options={map(commandOption.value as Option[], 'value')}
                      containerStyle={classes.agregateSelectContainer}
                      value={
                        (commandOption?.value as Option[])?.find((o) => o.key === createCommandType)
                          ?.value || ''
                      }
                      onChange={(value) =>
                        onCommandNameChange(commandOption.value as Option[], value)
                      }
                      disabled={!commandOption.selected}
                    />
                  )}
                  {(commandOption.type === 'text' || commandOption.type === 'number') &&
                    commandOption.selected && (
                      <TextInputTG
                        type={commandOption.type}
                        value={commandOption.value as string}
                        disabled={!commandOption.selected}
                        inputStyle={commandOption.style}
                        containerStyle={commandOption.style}
                        onChange={(value) => {
                          onChangeText(value, commandOption.label);
                        }}
                      />
                    )}
                  {commandOption.type === 'object' && commandOption.selected && (
                    <AdditionalActionsModal
                      commandOption={commandOption}
                      additionalActions={additionalActions}
                      onSaveActions={onSaveActions}
                      isCreationCommand={isCreationCommand}
                      aggregateTypeName={aggregateTypeName}
                    />
                  )}
                </Grid>
              );
            })}
          </Grid>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    selectContainer: {
      pointerEvents: 'auto',
      '&.MuiFormControl-root': {
        width: 142,
      },
      '& .MuiSelect-root': {
        height: 30,
        fontSize: 15,
      },
    },
    firstElement: {
      marginTop: 10,
    },
    rootContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    agregateSelectContainer: {
      '&.MuiFormControl-root': {
        width: 200,
        position: 'relative',
        left: 38,
        top: 3,
      },
      '& .MuiSelect-root': {
        height: 30,
        fontSize: 14,
      },
    },
    inputStyle: {
      border: `1px solid ${theme.palette.divider}`,
      fontSize: 15,
    },
    menuStyle: {
      fontSize: 15,
    },
    expandIcon: {
      pointerEvents: 'auto',
    },
    commandTypeContainer: {
      boxShadow: 'none',
      borderRadius: 5,
      padding: '5px 3px',
      '&.MuiAccordion-root': {
        position: 'static',
        margin: '10px 0px',
        marginTop: 0,
        backgroundColor: `${alpha(theme.palette.background.default, 0.2)} !important`,
      },
      '&:not(:last-child)': {
        borderBottom: 0,
      },
      '&:before': {
        display: 'none',
      },
      '&$expanded': {
        margin: 'auto',
      },
      '&.Mui-expanded': {
        marginTop: 0,
        marginBottom: 0,
      },
    },
    accordSummary: {
      flexDirection: 'row-reverse',
      alignItems: 'center',
      marginBottom: -1,
      padding: 10,
      paddingLeft: 2,
      minHeight: 45,
      height: 0,
      '&.Mui-expanded': {
        minHeight: 45,
      },
      '& .MuiAccordionSummary-expandIcon': {
        padding: 8,
        marginRight: 0,
        '&.Mui-expanded': {
          transform: 'rotate(90deg)',
        },
      },
      pointerEvents: 'none',
    },
    accordDetail: {
      padding: '11px 32px 17px 31px',
    },
    accordHead: {
      display: 'flex',
      alignItems: 'center',
    },
    commandTypeTxt: {
      fontSize: 15,
      color: theme.palette.text.primary,
      marginRight: 9,
      fontWeight: 500,
    },
    commandOptionContainer: {
      margin: '5px 0',
    },
    commandLabel: {
      fontSize: 14,
      fontWeight: 400,
      color: theme.palette.text.primary,
      marginRight: 10,
      flexShrink: 0,
    },
    intgrationCheckbox: {
      display: 'flex',
      alignItems: 'center',
    },
    fileTypeContainer: {
      margin: '5px 0',
    },
    fileTypeBox: {
      marginLeft: 35,
    },
    fileTypeLabel: {
      color: theme.palette.text.secondary,
      fontSize: 13,
      marginTop: 14,
      marginBottom: 5,
    },
    fileTypeInput: {
      fontSize: 14,
      width: 440,
    },
    checkBox: {
      width: 28,
      height: 28,
      marginRight: 8,
    },
  })
);

export default CommandTypeConfiguration;
